/**
 * useAffiliate (OCP-490) - shared hook that answers "is the current
 * account an affiliate, and what's its code/link/stats?". Used by the
 * Sidebar to conditionally show the Affiliate nav item and by the
 * AffiliatePage to render the dashboard.
 *
 * Caches the answer at module scope after the first fetch so the Sidebar
 * and the page share one round-trip. AuthContext clears the cache on
 * logout (via resetAffiliateCache) so a different customer logging in on
 * the same browser doesn't inherit the previous one's answer.
 *
 * `GET /v1/portal/affiliate` returns 200 with the affiliate payload when
 * the account is an affiliate, or 404 otherwise. A 404 is a normal
 * "not an affiliate" answer (affiliate=null), NOT an error.
 */
import { useEffect, useState } from 'react';

import { ApiError, api } from './api';

/** OCP-492 - per-currency commission balances (cents). */
export interface AffiliateEarnings {
  currency: string;
  holdingCents: number;
  eligibleCents: number;
  paidCents: number;
}

/** OCP-493 - one paid commission in the affiliate's payout history. */
export interface AffiliatePayout {
  id: string;
  amountCents: number;
  currency: string;
  paidAt: string;
  payoutRef: string | null;
}

export interface Affiliate {
  status: 'active' | 'paused';
  code: string | null;
  shareLink: string | null;
  stats: { referred: number; converted: number };
  /** OCP-492 - one entry per currency the affiliate has earned in (usually just EUR). */
  earnings: AffiliateEarnings[];
  /** OCP-493 - itemised paid commissions (payout history), newest first. */
  payoutHistory: AffiliatePayout[];
}

interface AffiliateWire {
  status: 'active' | 'paused';
  code: string | null;
  share_link: string | null;
  stats: { referred: number; converted: number };
  earnings?: {
    currencies: Array<{
      currency: string;
      holding_cents: number;
      eligible_cents: number;
      paid_cents: number;
    }>;
  };
  payout_history?: Array<{
    id: string;
    amount_cents: number;
    currency: string;
    paid_at: string;
    payout_ref: string | null;
  }>;
}

type CacheState =
  | { state: 'unknown' }
  | { state: 'loading'; promise: Promise<Affiliate | null> }
  | { state: 'resolved'; affiliate: Affiliate | null }
  | { state: 'error'; message: string };

let cache: CacheState = { state: 'unknown' };
const listeners = new Set<() => void>();

function notify(): void {
  for (const fn of listeners) fn();
}

async function fetchAffiliate(): Promise<Affiliate | null> {
  try {
    const r = await api<AffiliateWire>('/v1/portal/affiliate');
    return {
      status: r.status,
      code: r.code,
      shareLink: r.share_link,
      stats: r.stats,
      earnings: (r.earnings?.currencies ?? []).map((c) => ({
        currency: c.currency,
        holdingCents: c.holding_cents,
        eligibleCents: c.eligible_cents,
        paidCents: c.paid_cents,
      })),
      payoutHistory: (r.payout_history ?? []).map((p) => ({
        id: p.id,
        amountCents: p.amount_cents,
        currency: p.currency,
        paidAt: p.paid_at,
        payoutRef: p.payout_ref,
      })),
    };
  } catch (err) {
    // 404 = not an affiliate. Any other status is a real error.
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

function loadCache(): Promise<Affiliate | null> {
  if (cache.state === 'resolved') return Promise.resolve(cache.affiliate);
  if (cache.state === 'loading') return cache.promise;
  const promise = fetchAffiliate()
    .then((affiliate) => {
      cache = { state: 'resolved', affiliate };
      notify();
      return affiliate;
    })
    .catch((err: unknown) => {
      const message = err instanceof ApiError ? `HTTP ${err.status}` : 'fetch failed';
      cache = { state: 'error', message };
      notify();
      throw err;
    });
  cache = { state: 'loading', promise };
  notify();
  return promise;
}

export interface AffiliateState {
  loading: boolean;
  affiliate: Affiliate | null;
  error: string | null;
}

/**
 * @param enabled Gate the fetch (default true). The Sidebar passes
 *   `status === 'authenticated'` so the affiliate probe never fires before
 *   auth resolves (it would 401) or competes with the `/auth/me` request.
 */
export function useAffiliate(enabled = true): AffiliateState {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (enabled && cache.state === 'unknown') void loadCache().catch(() => undefined);
    const fn = () => setTick((n) => n + 1);
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  }, [enabled]);

  // Touch `tick` so React tracks the re-render dependency on cache changes.
  void tick;

  if (cache.state === 'resolved') {
    return { loading: false, affiliate: cache.affiliate, error: null };
  }
  if (cache.state === 'error') {
    return { loading: false, affiliate: null, error: cache.message };
  }
  return { loading: true, affiliate: null, error: null };
}

/**
 * Called from AuthContext on logout so a different customer landing on the
 * same browser session sees a fresh fetch instead of the previous
 * customer's cached answer.
 */
export function resetAffiliateCache(): void {
  cache = { state: 'unknown' };
  notify();
}
